import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Loader2 } from "lucide-react";
import GoogleAuthButton from "../components/app/GoogleAuthButton";
import PublicModelStrip from "../components/app/PublicModelStrip";
import LegalLinks from "../components/app/LegalLinks";
import { WangramIcon, WangramWordmark } from "../components/app/WangramLogo";

export default function Login() {
  const { user, login, loading } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
    else nav("/", { replace: true });
  };

  return (
    <AuthShell title={t("login.title")} eyebrow={t("login.eyebrow")}>
      <GoogleAuthButton label={t("login.google")} />
      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-white/10" />
        <div className="label-eyebrow">{t("login.or")}</div>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field label={t("login.email")} htmlFor="email">
          <input
            id="email"
            data-testid="login-email"
            type="email"
            required
            autoComplete="email"
            className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label={t("login.password")} htmlFor="password">
          <input
            id="password"
            data-testid="login-password"
            type="password"
            required
            autoComplete="current-password"
            className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error && (
          <div
            className="text-[12px] font-mono-p text-[#ff9c85] border border-[#ff9c85]/30 rounded px-3 py-2 bg-[#ff9c85]/5"
            data-testid="login-error"
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="btn-pill w-full h-11 justify-center focus-ring"
          data-testid="login-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("login.submit")}
        </button>
      </form>
      <div className="mt-6 label-eyebrow text-center">
        {t("login.noAccount")}{" "}
        <Link to="/register" className="underline hover:text-white" data-testid="link-to-register">
          {t("login.createOne")}
        </Link>
      </div>
      <div className="mt-2 label-eyebrow text-center">
        <Link to="/forgot-password" className="underline hover:text-white" data-testid="link-to-forgot">
          {t("login.forgot")}
        </Link>
      </div>
    </AuthShell>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="label-eyebrow block mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}

export function AuthShell({ children, title, eyebrow }) {
  const { t } = useLang();
  return (
    <div
      className="min-h-screen w-full relative flex items-start justify-center px-4 py-10 overflow-hidden"
      style={{
        backgroundImage:
          "linear-gradient(rgba(10,10,10,0.78), rgba(10,10,10,0.9)), url(https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&w=2400&q=70)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Large watermark logo behind everything */}
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
        aria-hidden="true"
      >
        <WangramIcon
          className="w-[min(85vw,780px)] h-[min(85vw,780px)] opacity-[0.06]"
          ariaLabel=""
        />
      </div>
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(600px 400px at 50% 30%, rgba(42,219,128,0.08), transparent 70%)"
      }} />

      <div className="relative w-full max-w-md">
        {/* Enlarged, centered wordmark badge at top */}
        <div className="mb-8 flex justify-center" data-testid="auth-brand-badge">
          <WangramWordmark className="w-80 md:w-[26rem] h-auto drop-shadow-[0_10px_40px_rgba(42,219,128,0.3)]" />
        </div>
        <div className="rounded-lg border border-white/10 bg-black/60 backdrop-blur-xl p-7">
          <div className="label-eyebrow mb-2">{eyebrow}</div>
          <h1 className="font-display text-3xl font-black tracking-tight mb-2">{title}</h1>
          <div className="label-eyebrow mb-6 opacity-70">
            {t("login.shellNote")}
          </div>
          {children}
        </div>
        <PublicModelStrip />
        <div className="mt-6 label-eyebrow text-center opacity-60">
          {t("login.registeredOnly")}
        </div>
        <div className="mt-3">
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}
